import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { AlertCircleIcon, CheckCircleIcon, Loader2Icon, BellIcon, ShieldIcon } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/lib/supabase";
import { AccountService } from "@/lib/profile-service";
import { NotificationService } from "@/lib/notification-service";

export default function UserProfilePage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState("general");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    companyName: "",
    address: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    userType: "",
    avatar: "",
    isVerified: false,
    isAdmin: false,
    createdAt: "",
    updatedAt: "",
    // Additional fields from supplier_profiles table
    gstNumber: "",
    panNumber: "",
    supplierAddress: "", // New field for supplier_profiles.address
  });
  
  // User preferences state
  const [preferences, setPreferences] = useState({
    notificationNewInquiries: true,
    notificationNewOrders: true,
    notificationMarketing: true,
    privacyProfileVisibility: true,
    privacyContactVisibility: false,
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else {
      loadUserProfile();
      loadUserPreferences();
    }
  }, [user, navigate]);
  
  // Load user preferences
  const loadUserPreferences = async () => {
    if (!user?.id) return;
    
    try {
      const notificationService = new NotificationService();
      const userPrefs = await notificationService.getUserPreferences(user.id);
      
      if (userPrefs) {
        setPreferences({
          notificationNewInquiries: userPrefs.notification_new_inquiries,
          notificationNewOrders: userPrefs.notification_new_orders,
          notificationMarketing: userPrefs.notification_marketing,
          privacyProfileVisibility: userPrefs.privacy_profile_visibility,
          privacyContactVisibility: userPrefs.privacy_contact_visibility,
        });
      }
    } catch (err) {
      console.error("Error loading user preferences:", err);
    }
  };

  const loadUserProfile = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const accountService = new AccountService();
      const accountData = await accountService.getAccount(user.id);
      
      if (!accountData) {
        setError("Could not load profile. Please try again.");
        return;
      }
      
      // Split full name into first and last name
      let firstName = "";
      let lastName = "";
      
      if (accountData.full_name) {
        const nameParts = accountData.full_name.split(' ');
        firstName = nameParts[0] || "";
        lastName = nameParts.slice(1).join(' ') || "";
      }
      
      // Get supplier profile data if user is a supplier or both
      let supplierData: { gst_number?: string; pan_number?: string; address?: string } = {};
      if (accountData.user_type === 'supplier' || accountData.user_type === 'both') {
        try {
          const { data: supplierProfileData } = await supabase
            .from('supplier_profiles')
            .select('*')
            .eq('id', user.id)
            .single();
            
          if (supplierProfileData) {
            supplierData = supplierProfileData;
          }
        } catch (err) {
          console.log('No supplier profile found or user is not a supplier');
        }
      }
      
      setProfile({
        firstName,
        lastName,
        email: accountData.email || "",
        phone: accountData.phone || "",
        companyName: accountData.company_name || "",
        address: accountData.address || "",
        city: accountData.city || "",
        state: accountData.state || "",
        country: accountData.country || "",
        pincode: accountData.pincode || "",
        userType: accountData.user_type || "",
        avatar: accountData.avatar_url || "",
        isVerified: accountData.is_verified || false,
        isAdmin: accountData.is_admin || false,
        createdAt: accountData.created_at || "",
        updatedAt: accountData.updated_at || "",
        // Additional fields from supplier_profiles table
        gstNumber: supplierData?.gst_number || "",
        panNumber: supplierData?.pan_number || "",
        supplierAddress: supplierData?.address || "",
      });
    } catch (err) {
      console.error("Error loading profile:", err);
      setError("An error occurred while loading your profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  // Handle switch toggle changes for boolean fields
  const handleSwitchChange = (checked: boolean, name: string) => {
    setPreferences(prev => ({ ...prev, [name]: checked }));
  };

  const handleRadioChange = (value: string) => {
    setProfile(prev => ({ ...prev, userType: value }));
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      
      // Prepare full name
      const fullName = `${profile.firstName} ${profile.lastName}`.trim();
      
      // Update user profile in accounts table
      const { error } = await supabase
        .from('accounts')
        .update({
          full_name: fullName,
          email: profile.email,
          phone: profile.phone,
          company_name: profile.companyName,
          address: profile.address,
          city: profile.city,
          state: profile.state,
          country: profile.country,
          pincode: profile.pincode,
          user_type: profile.userType,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
        
      // If user is a supplier or both, update supplier_profiles table
      if (profile.userType === 'supplier' || profile.userType === 'both') {
        // Check if supplier profile exists
        const { data: existingSupplierProfile } = await supabase
          .from('supplier_profiles')
          .select('id')
          .eq('id', user.id)
          .single();
          
        if (existingSupplierProfile) {
          // Update existing supplier profile
          await supabase
            .from('supplier_profiles')
            .update({
              gst_number: profile.gstNumber,
              pan_number: profile.panNumber,
              address: profile.supplierAddress,
              updated_at: new Date().toISOString()
            })
            .eq('id', user.id);
        } else {
          // Create new supplier profile
          await supabase
            .from('supplier_profiles')
            .insert({
              id: user.id,
              business_name: profile.companyName,
              gst_number: profile.gstNumber,
              pan_number: profile.panNumber,
              address: profile.supplierAddress,
              slug: profile.companyName.toLowerCase().replace(/\s+/g, '-'),
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
        }
      }
      
      // Update user preferences
      const notificationService = new NotificationService();
      await notificationService.updateUserPreferences(user.id, {
        notification_new_inquiries: preferences.notificationNewInquiries,
        notification_new_orders: preferences.notificationNewOrders,
        notification_marketing: preferences.notificationMarketing,
        privacy_profile_visibility: preferences.privacyProfileVisibility,
        privacy_contact_visibility: preferences.privacyContactVisibility,
      });
      
      if (error) throw error;
      
      // Refresh user data
      await refreshUser();
      
      setSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      console.error("Error saving profile:", err);
      setError("An error occurred while saving your profile.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2Icon className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Loading your profile...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex flex-col space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold">My Account</h1>
            <p className="text-muted-foreground">Manage your account settings and preferences</p>
          </div>
          
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={profile.avatar || "https://github.com/polymet-ai.png"} alt="Profile" />
              <AvatarFallback>
                {(profile.firstName && typeof profile.firstName === 'string') ? profile.firstName.charAt(0) : ''}
                {(profile.lastName && typeof profile.lastName === 'string') ? profile.lastName.charAt(0) : 'U'}
              </AvatarFallback>
            </Avatar>
            
            <div>
              <h2 className="text-xl font-semibold">{profile.firstName} {profile.lastName}</h2>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={profile.userType === 'supplier' ? "default" : profile.userType === 'buyer' ? "secondary" : "outline"}>
                  {profile.userType === 'supplier' ? 'Supplier' : profile.userType === 'buyer' ? 'Buyer' : profile.userType === 'both' ? 'Supplier & Buyer' : 'User'}
                </Badge>
              </div>
            </div>
          </div>
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="business">Business Info</TabsTrigger>
            <TabsTrigger value="account">Account Type</TabsTrigger>
            <TabsTrigger value="preferences">Preferences</TabsTrigger>
          </TabsList>
          
          {error && (
            <div className="bg-destructive/10 text-destructive p-4 rounded-md mb-4 flex items-center gap-2">
              <AlertCircleIcon className="h-5 w-5" />
              <p>{error}</p>
            </div>
          )}
          
          {success && (
            <div className="bg-green-100 text-green-800 p-4 rounded-md mb-4 flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5" />
              <p>Profile updated successfully!</p>
            </div>
          )}
          
          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>Update your personal details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input 
                      id="firstName" 
                      name="firstName" 
                      value={profile.firstName} 
                      onChange={handleInputChange} 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input 
                      id="lastName" 
                      name="lastName" 
                      value={profile.lastName} 
                      onChange={handleInputChange} 
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input 
                      id="email" 
                      name="email" 
                      type="email" 
                      value={profile.email} 
                      onChange={handleInputChange} 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input 
                      id="phone" 
                      name="phone" 
                      value={profile.phone} 
                      onChange={handleInputChange} 
                    />
                  </div>
                </div>
                
                <div className="flex flex-col space-y-2 mt-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="isVerified" className="text-sm font-medium">Verification Status</Label>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${profile.isVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {profile.isVerified ? 'Verified' : 'Pending Verification'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Member Since</Label>
                    <span className="text-sm text-gray-500">
                      {profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Last Updated</Label>
                    <span className="text-sm text-gray-500">
                      {profile.updatedAt ? new Date(profile.updatedAt).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                <Button onClick={handleSaveProfile} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : "Save Changes"}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="business">
            <Card>
              <CardHeader>
                <CardTitle>Business Information</CardTitle>
                <CardDescription>Update your business details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name</Label>
                  <Input 
                    id="companyName" 
                    name="companyName" 
                    value={profile.companyName} 
                    onChange={handleInputChange} 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="address">Personal Address</Label>
                  <Textarea 
                    id="address" 
                    name="address" 
                    value={profile.address} 
                    onChange={handleInputChange} 
                  />
                </div>
                
                {(profile.userType === 'supplier' || profile.userType === 'both') && (
                  <div className="space-y-2">
                    <Label htmlFor="supplierAddress" className="flex items-center">
                      Business Address
                      <span className="ml-2 text-xs text-muted-foreground">(For supplier profile)</span>
                    </Label>
                    <Textarea 
                      id="supplierAddress" 
                      name="supplierAddress" 
                      value={profile.supplierAddress} 
                      onChange={handleInputChange} 
                      placeholder="Enter your business address if different from personal address"
                    />
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input 
                      id="city" 
                      name="city" 
                      value={profile.city} 
                      onChange={handleInputChange} 
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input 
                      id="state" 
                      name="state" 
                      value={profile.state} 
                      onChange={handleInputChange} 
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Input 
                      id="country" 
                      name="country" 
                      value={profile.country} 
                      onChange={handleInputChange} 
                      placeholder="Default: India"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Postal/ZIP Code</Label>
                    <Input 
                      id="pincode" 
                      name="pincode" 
                      value={profile.pincode} 
                      onChange={handleInputChange} 
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="gstNumber">GST Number</Label>
                  <Input 
                    id="gstNumber" 
                    name="gstNumber" 
                    value={profile.gstNumber} 
                    onChange={handleInputChange} 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="panNumber">PAN Number</Label>
                  <Input 
                    id="panNumber" 
                    name="panNumber" 
                    value={profile.panNumber} 
                    onChange={handleInputChange} 
                  />
                </div>
                

              </CardContent>
              <CardFooter className="flex justify-end">
                <Button onClick={handleSaveProfile} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : "Save Changes"}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="account">
            <Card>
              <CardHeader>
                <CardTitle>Account Type</CardTitle>
                <CardDescription>Select your account type to access different features</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <RadioGroup 
                  value={profile.userType} 
                  onValueChange={handleRadioChange} 
                  className="space-y-4"
                >
                  <div className="flex items-start space-x-3 p-4 border rounded-md">
                    <RadioGroupItem value="buyer" id="buyer" className="mt-1" />
                    <div className="grid gap-1.5">
                      <Label htmlFor="buyer" className="font-medium">Buyer</Label>
                      <p className="text-sm text-muted-foreground">
                        Access buyer features like sending inquiries, requesting quotes, and managing orders.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-4 border rounded-md">
                    <RadioGroupItem value="both" id="both" className="mt-1" />
                    <div className="grid gap-1.5">
                      <Label htmlFor="both" className="font-medium">Both (Buyer & Supplier)</Label>
                      <p className="text-sm text-muted-foreground">
                        Access all features available to both buyers and suppliers, including listing products and responding to inquiries.
                      </p>
                    </div>
                  </div>
                </RadioGroup>
                
                <Separator />
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium">Buyer Dashboard</h3>
                      <p className="text-sm text-muted-foreground">
                        {profile.userType === 'buyer' || profile.userType === 'both' 
                          ? 'You have access to the buyer dashboard' 
                          : 'Change your account type to access the buyer dashboard'}
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      onClick={() => navigate('/buyer-dashboard')}
                      disabled={profile.userType !== 'buyer' && profile.userType !== 'both'}
                    >
                      Go to Buyer Dashboard
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium">Supplier Dashboard</h3>
                      <p className="text-sm text-muted-foreground">
                        {profile.userType === 'supplier' || profile.userType === 'both' 
                          ? 'You have access to the supplier dashboard' 
                          : 'Change your account type to access the supplier dashboard'}
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      onClick={() => navigate('/supplier-dashboard')}
                      disabled={profile.userType !== 'supplier' && profile.userType !== 'both'}
                    >
                      Go to Supplier Dashboard
                    </Button>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                <Button onClick={handleSaveProfile} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : "Save Changes"}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
          
          <TabsContent value="preferences">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BellIcon className="h-5 w-5" />
                  Notifications
                </CardTitle>
                <CardDescription>Manage your notification preferences</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="notification-inquiries">New Inquiries</Label>
                      <p className="text-sm text-muted-foreground">Get notified when you receive new inquiries</p>
                    </div>
                    <Switch
                      id="notification-inquiries"
                      checked={preferences.notificationNewInquiries}
                      onCheckedChange={(checked) => handleSwitchChange(checked, 'notificationNewInquiries')}
                    />
                  </div>
                  
                  <Separator />
                  
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="notification-orders">New Orders</Label>
                      <p className="text-sm text-muted-foreground">Get notified when you receive new orders</p>
                    </div>
                    <Switch
                      id="notification-orders"
                      checked={preferences.notificationNewOrders}
                      onCheckedChange={(checked) => handleSwitchChange(checked, 'notificationNewOrders')}
                    />
                  </div>
                  
                  <Separator />
                  
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="notification-marketing">Marketing Updates</Label>
                      <p className="text-sm text-muted-foreground">Receive marketing and promotional emails</p>
                    </div>
                    <Switch
                      id="notification-marketing"
                      checked={preferences.notificationMarketing}
                      onCheckedChange={(checked) => handleSwitchChange(checked, 'notificationMarketing')}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldIcon className="h-5 w-5" />
                  Privacy
                </CardTitle>
                <CardDescription>Manage your privacy settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="privacy-profile">Profile Visibility</Label>
                      <p className="text-sm text-muted-foreground">Make your profile visible to all buyers</p>
                    </div>
                    <Switch
                      id="privacy-profile"
                      checked={preferences.privacyProfileVisibility}
                      onCheckedChange={(checked) => handleSwitchChange(checked, 'privacyProfileVisibility')}
                    />
                  </div>
                  
                  <Separator />
                  
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="privacy-contact">Contact Information</Label>
                      <p className="text-sm text-muted-foreground">Show your contact information to verified buyers</p>
                    </div>
                    <Switch
                      id="privacy-contact"
                      checked={preferences.privacyContactVisibility}
                      onCheckedChange={(checked) => handleSwitchChange(checked, 'privacyContactVisibility')}
                    />
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                <Button onClick={handleSaveProfile} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : "Save Changes"}
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
